import { z } from 'zod';

// Body of the "Your time is up" view (LUM-72). Operator-editable via
// the script version's copy JSON; this is the fallback.
const DEFAULT_TIME_CAPPED_TEXT =
  "We capture the full conversation up to the time limit — your interview has been saved. If you had more to share, please email [dialogue-help@luminarium.ai](mailto:dialogue-help@luminarium.ai) and we'll help you.";

const DEFAULT_INTRO_CARD_TEXT =
  "Welcome to the Discovery Dialogue. We're excited to learn about your experiences with AI tools and how they've impacted your work.";
const DEFAULT_INTERVIEW_TIMING_TEXT =
  "This interview typically takes 10–15 minutes. Please ensure you're in a quiet space with a stable internet connection.";
const DEFAULT_COMPLETION_TEXT =
  "Thank you for completing the Discovery Dialogue! Your insights are invaluable. We'll follow up with next steps via email within 2-3 business days.";

// Structured copy (LUM-158): one titled cell of a hairline strip. Caps
// mirror the dd-admin script-version ingest schema.
const CopyCellSchema = z.object({
  title: z.string().min(1).max(120),
  detail: z.string().max(500),
});

const PracticalitiesSchema = z.object({
  duration: CopyCellSchema,
  environment: CopyCellSchema,
  connection: CopyCellSchema,
});

const CompletionSchema = z.object({
  title: z.string().min(1).max(120),
  body: z.string().max(1000),
  nextSteps: z.array(CopyCellSchema).max(3),
});

export type CopyCell = z.infer<typeof CopyCellSchema>;

export const UxCopySchema = z.object({
  intro_card_text: z.string().max(2000).default(DEFAULT_INTRO_CARD_TEXT),
  interview_timing_text: z
    .string()
    .max(1000)
    .default(DEFAULT_INTERVIEW_TIMING_TEXT),
  completion_text: z.string().max(1000).default(DEFAULT_COMPLETION_TEXT),
  time_capped_text: z.string().max(1000).default(DEFAULT_TIME_CAPPED_TEXT),
  // Optional structured copy. An invalid value is dropped (→ undefined)
  // rather than failing the whole parse, so an org's blobs survive a
  // malformed structured field and render as before.
  practicalities: PracticalitiesSchema.optional().catch(undefined),
  completion: CompletionSchema.optional().catch(undefined),
});

export type UxCopy = z.infer<typeof UxCopySchema>;

export const DEFAULT_UX_COPY: UxCopy = {
  intro_card_text: DEFAULT_INTRO_CARD_TEXT,
  interview_timing_text: DEFAULT_INTERVIEW_TIMING_TEXT,
  completion_text: DEFAULT_COMPLETION_TEXT,
  time_capped_text: DEFAULT_TIME_CAPPED_TEXT,
  // Same meaning as the default blobs. The call cap is server-owned
  // (GET /api/realtime/limits), so the duration cell states the typical
  // length the default blob already quotes and never pins the limit.
  practicalities: {
    duration: {
      title: 'About 10–15 minutes',
      detail: "Typical length — you'll get a heads-up before the time limit.",
    },
    environment: {
      title: 'A quiet space',
      detail: 'Somewhere you can talk without interruptions.',
    },
    connection: {
      title: 'A stable connection',
      detail: 'A steady internet connection keeps the audio clear.',
    },
  },
  completion: {
    title: 'Thank you for completing the Discovery Dialogue',
    body: 'Your insights are invaluable.',
    nextSteps: [
      {
        title: "We'll be in touch",
        detail:
          "We'll follow up with next steps via email within 2–3 business days.",
      },
    ],
  },
};

/**
 * A profile's stored ux_copy → the copy the interview renders.
 *
 * Fallback rule (LUM-158), per area (practicalities ↔ interview_timing_text,
 * completion ↔ completion_text):
 *  1. the profile's own valid structured field wins;
 *  2. otherwise, if the profile carries a custom blob for that area (key
 *     present with text other than the default blob), the structured field
 *     stays undefined and the blob renders exactly as before;
 *  3. otherwise (no blob, or the default blob verbatim) the default
 *     structured copy is used.
 * Missing or unparsable copy → DEFAULT_UX_COPY.
 */
export function resolveUxCopy(raw: unknown): UxCopy {
  const parsed = UxCopySchema.safeParse(raw);
  if (!parsed.success) return DEFAULT_UX_COPY;
  const copy = parsed.data;
  return {
    ...copy,
    practicalities:
      copy.practicalities ??
      (copy.interview_timing_text === DEFAULT_INTERVIEW_TIMING_TEXT
        ? DEFAULT_UX_COPY.practicalities
        : undefined),
    completion:
      copy.completion ??
      (copy.completion_text === DEFAULT_COMPLETION_TEXT
        ? DEFAULT_UX_COPY.completion
        : undefined),
  };
}
